import { useTenant } from '@rewardops/auth'
import {
  PromotionBannerProps,
  type ContentfulAdBanner,
  type FaqList,
  type HeroBannerProps,
  type Section,
  type Table,
} from '@rewardops/utils'

import { ElpRoleBasedDashboard } from '../elp-dashboard/role-based-dashboard'
import { ElpPromotions } from '../elp-promotions'
import { FeaturedProducts } from '../featured-carousel/featured-products'
import { FeaturedRetailers } from '../featured-carousel/featured-retailers'
import { HeroBanner } from '../hero-banner/hero-banner'
import { Steps } from '../maple/components/steps'
import { EarnMessagingBanner } from '../maple/pages/checkout/earn-messaging-banner'
import { WishlistEarnBanner } from '../maple/pages/wishlist/earn-banner'
import { Markdown } from '../markdown/markdown'
import { RenderMarkdown } from '../markdown/render-markdown'
import { PageBanner } from '../page-banner/page-banner'
import { ProductPromotionBanner } from '../promotion-banner/product-promotion-banner'
import { RetailerPromotionBanner } from '../promotion-banner/retailer-promotion-banner'
import { RecommendedItems } from '../recommended-items/recommended-items'
import { VideoBanner } from '../video-banner/video-banner'
import AccordionList from './accordionList'
import AdBanner from './adBanner'
import FullWidthCard from './fullwidthCard'
import FullWidthPane from './fullWidthPane'
import FullWidthPaneSmall from './fullWidthPaneSmall'
import { GenericStickyBanner } from './generic-sticky-banner'
import HalfWidthPane from './halfWidthPane'
import DynamicTable from './table'
import { WidgetContainer } from './widget-container'

type Props = {
  content: Section
}

function ContentfulSection({ content }: Props) {
  const tenant = useTenant()
  if (content.type === 'HomePageFullWidth')
    return <FullWidthPane content={content} />

  if (content.type === 'HomePageHalfWidth')
    return <HalfWidthPane content={content} />

  if (content.type === 'FullWidth')
    return <FullWidthPaneSmall content={content} />

  if (content._type === 'faqList')
    return <AccordionList content={content as FaqList} />

  if (content?._type === 'featuredCarousel') {
    // for now we have only 2 types of carousels, products & retailers
    switch (content?.type) {
      case 'products':
        return <FeaturedProducts content={content} />
      case 'retailers':
        return tenant?.flags?.retailers ? (
          <FeaturedRetailers content={content} />
        ) : null
      default:
        return null
    }
  }

  if (content?._type === 'table') {
    return <DynamicTable content={content as unknown as Table} />
  }

  if (content?._type === 'heroBanner') {
    return <HeroBanner content={content as unknown as HeroBannerProps} />
  }

  if (content?._type === 'promotionBanner') {
    return content?.isProduct ? (
      <ProductPromotionBanner
        content={content as unknown as PromotionBannerProps}
      />
    ) : tenant?.flags?.retailers ? (
      <RetailerPromotionBanner
        content={content as unknown as PromotionBannerProps}
      />
    ) : null
  }

  if (content?._type === 'elpPromotionsContainer') {
    return <ElpPromotions content={content} />
  }

  if (content?._type === 'videoBannerSlide') {
    return <VideoBanner content={content} />
  }

  if (content._type === 'fullWidthCard') {
    return <FullWidthCard content={content} />
  }

  if (content?._type === 'itemRecommendations') {
    return <RecommendedItems content={content as any} />
  }

  if (content?._type === 'faqItem') {
    return <Markdown content={content as any} />
  }

  if (content?._type === 'rawMarkdown') {
    return <RenderMarkdown content={(content as any)?.markdown} />
  }

  if (content?._type === 'pageBanner') {
    return <PageBanner content={content as any} />
  }

  if (content?._type === 'stepper') {
    return <Steps steps={content as any} />
  }

  if (content?._type === 'genericStickyBanner') {
    return <GenericStickyBanner content={content as any} />
  }

  if (content?._type === 'widget') {
    return <WidgetContainer content={content} />
  }

  if (content?._type === 'earnMessagingBanner') {
    return (content as any)?.icon ? (
      <div className="col-span-2 mx-auto mb-[100px] w-full max-w-[1140px]">
        <EarnMessagingBanner content={content as any} />
      </div>
    ) : (
      <WishlistEarnBanner content={content as any} />
    )
  }

  if (content?._type === 'pawDashboard') {
    return (
      <div className="col-span-2 w-full">
        <ElpRoleBasedDashboard content={content as any} />
      </div>
    )
  }

  if (content?._type === 'widget') {
    return <WidgetContainer content={content} />
  }

  return content._type === 'adBanner' ? (
    <AdBanner content={content as ContentfulAdBanner} />
  ) : undefined
}

export default ContentfulSection
